import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Box,
  Text,
  VStack,
  HStack,
  Divider,
  useColorModeValue,
  Code,
  Spinner,
  Center,
  Select,
  Badge,
  Progress,
} from '@chakra-ui/react';
import { useLinkAnalytics } from '../../hooks/useLinks';
import { formatNumber } from '../../utils/format';

function LinkStatsModal({ isOpen, onClose, link }) {
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const [period, setPeriod] = useState('7d');
  const serverUrl = window.location.origin.replace(':3058', ':3059');

  const { data: analytics, loading, error } = useLinkAnalytics(
    link?.link_id,
    period
  );

  if (!link) return null;

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTopItem = (items, key) => {
    if (!items || items.length === 0) return '-';
    return items[0][key] || '-';
  };

  const renderBreakdown = (items, labelKey, countKey, colorScheme = 'brand') => {
    if (!items || items.length === 0) {
      return <Text color="gray.500" fontSize="sm">No data</Text>;
    }

    const total = items.reduce((sum, item) => sum + (parseInt(item[countKey]) || 0), 0);

    return (
      <VStack spacing={2} align="stretch">
        {items.slice(0, 5).map((item, index) => {
          const value = item[labelKey] || 'Unknown';
          const count = parseInt(item[countKey]) || 0;
          const percentage = total > 0 ? (count / total) * 100 : 0;

          return (
            <Box key={index}>
              <HStack justify="space-between" mb={1}>
                <Text fontSize="sm" noOfLines={1}>{value}</Text>
                <Text fontSize="sm" color="gray.500">{formatNumber(count)}</Text>
              </HStack>
              <Progress
                value={percentage}
                size="xs"
                colorScheme={colorScheme}
                borderRadius="full"
              />
            </Box>
          );
        })}
      </VStack>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" mx={4}>
        <ModalHeader>
          <VStack align="start" spacing={1} pr={8}>
            <Text>{link.link_name}</Text>
            <Code fontSize="sm" bg="transparent" p={0}>
              {serverUrl}/l/{link.short_code}
            </Code>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Period Selector */}
          <HStack mb={6}>
            <Text fontSize="sm" fontWeight="500">Time Period:</Text>
            <Select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              size="sm"
              w="150px"
              borderRadius="lg"
            >
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="all">All time</option>
            </Select>
          </HStack>

          {loading ? (
            <Center py={12}>
              <Spinner size="lg" color="brand.500" />
            </Center>
          ) : error ? (
            <Center py={12}>
              <Text color="red.500">Error loading analytics: {error}</Text>
            </Center>
          ) : (
            <VStack spacing={6} align="stretch">
              {/* Main Stats */}
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                <Box bg={cardBg} p={4} borderRadius="xl">
                  <Stat>
                    <StatLabel color="gray.500">Total Clicks</StatLabel>
                    <StatNumber fontSize="2xl">
                      {formatNumber(analytics?.totalClicks || 0)}
                    </StatNumber>
                  </Stat>
                </Box>
                <Box bg={cardBg} p={4} borderRadius="xl">
                  <Stat>
                    <StatLabel color="gray.500">Unique Visitors</StatLabel>
                    <StatNumber fontSize="2xl">
                      {formatNumber(analytics?.uniqueVisitors || 0)}
                    </StatNumber>
                  </Stat>
                </Box>
                <Box bg={cardBg} p={4} borderRadius="xl">
                  <Stat>
                    <StatLabel color="gray.500">Top Device</StatLabel>
                    <StatNumber fontSize="lg">
                      {getTopItem(analytics?.devices, 'device_type')}
                    </StatNumber>
                  </Stat>
                </Box>
                <Box bg={cardBg} p={4} borderRadius="xl">
                  <Stat>
                    <StatLabel color="gray.500">Top Country</StatLabel>
                    <StatNumber fontSize="lg">
                      {getTopItem(analytics?.countries, 'country_code')}
                    </StatNumber>
                  </Stat>
                </Box>
              </SimpleGrid>

              <Divider />

              {/* Breakdown Grids */}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Box>
                  <Text fontWeight="600" mb={3}>Devices</Text>
                  {renderBreakdown(analytics?.devices, 'device_type', 'count', 'blue')}
                </Box>
                <Box>
                  <Text fontWeight="600" mb={3}>Browsers</Text>
                  {renderBreakdown(analytics?.browsers, 'browser', 'count', 'purple')}
                </Box>
                <Box>
                  <Text fontWeight="600" mb={3}>Operating Systems</Text>
                  {renderBreakdown(analytics?.operatingSystems, 'os', 'count', 'green')}
                </Box>
                <Box>
                  <Text fontWeight="600" mb={3}>Countries</Text>
                  {renderBreakdown(analytics?.countries, 'country_code', 'count', 'orange')}
                </Box>
              </SimpleGrid>

              {analytics?.referrers && analytics.referrers.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Text fontWeight="600" mb={3}>Top Referrers</Text>
                    {renderBreakdown(analytics.referrers, 'referrer_url', 'count', 'teal')}
                  </Box>
                </>
              )}

              <Divider />

              {/* Link Details */}
              <SimpleGrid columns={2} spacing={4}>
                <Box>
                  <Text fontSize="sm" color="gray.500" mb={1}>Destination URL</Text>
                  <Text fontWeight="500" fontSize="sm" noOfLines={2}>
                    {link.destination_url}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500" mb={1}>Created</Text>
                  <Text fontWeight="500">{formatDate(link.created_at)}</Text>
                </Box>
              </SimpleGrid>

              {/* UTM Tags */}
              {(link.utm_source || link.utm_medium || link.utm_campaign || link.utm_term || link.utm_content) && (
                <>
                  <Divider />
                  <Box>
                    <Text fontWeight="600" mb={3}>UTM Parameters</Text>
                    <HStack spacing={2} flexWrap="wrap">
                      {link.utm_source && (
                        <Badge colorScheme="blue" borderRadius="full">
                          source: {link.utm_source}
                        </Badge>
                      )}
                      {link.utm_medium && (
                        <Badge colorScheme="purple" borderRadius="full">
                          medium: {link.utm_medium}
                        </Badge>
                      )}
                      {link.utm_campaign && (
                        <Badge colorScheme="green" borderRadius="full">
                          campaign: {link.utm_campaign}
                        </Badge>
                      )}
                      {link.utm_term && (
                        <Badge colorScheme="orange" borderRadius="full">
                          term: {link.utm_term}
                        </Badge>
                      )}
                      {link.utm_content && (
                        <Badge colorScheme="teal" borderRadius="full">
                          content: {link.utm_content}
                        </Badge>
                      )}
                    </HStack>
                  </Box>
                </>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} borderRadius="xl">
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default LinkStatsModal;
